/**
 * usePermissions – Laedt die effektiven Berechtigungen des aktuellen Benutzers.
 *
 * Nutzt GET /rbac/my-permissions (kein Admin-Recht noetig).
 * Stellt Hilfsfunktionen bereit:
 *   - hasPermission('expert-profile.admin')
 *   - hasAnyPermission(['expert-profile.admin', 'expert-profile.edit-all'])
 *   - isModuleAdmin('expert-profile')
 */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import api from '../api/client';

interface UsePermissionsResult {
  permissions: string[];
  isLoading: boolean;
  hasPermission: (key: string) => boolean;
  hasAnyPermission: (keys: string[]) => boolean;
  isModuleAdmin: (moduleKey: string) => boolean;
}

export function usePermissions(): UsePermissionsResult {
  const { isAuthenticated } = useAuth();

  const { data, isLoading } = useQuery<string[]>({
    queryKey: ['rbac', 'my-permissions'],
    queryFn: async () => {
      const res = await api.get<{ permissions: string[] }>('/rbac/my-permissions');
      return res.data.permissions;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 Minuten
  });

  const permissions = data ?? [];

  return {
    permissions,
    isLoading,
    hasPermission: (key: string) => permissions.includes(key),
    hasAnyPermission: (keys: string[]) => keys.some((k) => permissions.includes(k)),
    isModuleAdmin: (moduleKey: string) => permissions.includes(`${moduleKey}.admin`),
  };
}
